import { useLang } from "../i18n/LangContext";
import { useReveal } from "../hooks/useReveal";
import { Check } from "lucide-react";

const ABOUT_IMG =
    "https://images.unsplash.com/photo-1738918937796-743064feefa1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
    const { t } = useLang();
    const { ref, className } = useReveal();
    return (
        <section
            id="about"
            data-testid="about-section"
            className="py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div
                    ref={ref}
                    className={`${className} grid lg:grid-cols-12 gap-12 lg:gap-16 items-start`}
                >
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-3 border border-ink hidden sm:block" />
                            <img
                                src={ABOUT_IMG}
                                alt="GTB control room"
                                className="relative w-full aspect-[4/5] object-cover grayscale"
                            />
                            <div className="absolute -bottom-4 -left-4 bg-mint p-4 hidden sm:block max-w-[200px]">
                                <div className="font-display text-xs uppercase tracking-widest text-ink">
                                    100+
                                </div>
                                <div className="font-display font-bold text-base uppercase text-ink leading-tight mt-1">
                                    Marques partenaires mondiales
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-px bg-ink" />
                            <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                                {t.about.kicker}
                            </span>
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
                            {t.about.title}
                        </h2>

                        <div className="mt-8 space-y-5">
                            {t.about.paragraphs.map((p, i) => (
                                <p
                                    key={i}
                                    className="text-base md:text-lg text-ink-soft leading-relaxed"
                                >
                                    {p}
                                </p>
                            ))}
                        </div>

                        {/* Mission */}
                        <div className="mt-12 bg-paper-alt border-l-4 border-mint p-6 md:p-8">
                            <h3
                                data-testid="about-mission-title"
                                className="font-display font-bold uppercase text-xl md:text-2xl tracking-tight text-ink"
                            >
                                {t.about.mission.title}
                            </h3>
                            <ul className="mt-5 space-y-3">
                                {t.about.mission.items.map((it, i) => (
                                    <li
                                        key={i}
                                        data-testid={`mission-${i}`}
                                        className="flex items-start gap-3 text-sm md:text-base text-ink"
                                    >
                                        <span className="w-6 h-6 bg-ink text-mint flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Check size={14} />
                                        </span>
                                        <span className="leading-relaxed">
                                            {it}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-ink">
                            {t.about.values.map((v, i) => (
                                <div
                                    key={i}
                                    data-testid={`value-${i}`}
                                    className="border-r border-b border-ink p-6 bg-white"
                                >
                                    <div className="font-display font-bold uppercase text-base lg:text-lg tracking-tight text-ink leading-tight">
                                        {v.title}
                                    </div>
                                    <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
